import { ALL_FILTER_VALUES_CLEARED } from '../../../filterQueryFormat/UsageSubActionTypes';
import { USED_LEGACY_FILTERS_BAR } from '../../../filterQueryFormat/UsageActionTypes';
import { List, OrderedMap } from 'immutable';
import { getQuickFilterComponent } from '../utils/getQuickFilterComponent';
import { mediumButtonProperties, smallButtonProperties } from './constants/quickFilterButtonProperties';
import { useContext, useState, useEffect, useMemo } from 'react';
import { useOnQuickFilterValueChange } from '../../../hooks/useOnQuickFilterValueChange';
import { useQuickFiltersOnChangeHandler } from '../../../hooks/useQuickFiltersOnChangeHandler';
import { useStrategy } from '../../../hooks/useStrategy';
import { useTranslationHelpers } from '../../../hooks/useTranslationHelpers';
import DSFieldRecord from '../../../filterQueryFormat/DSFieldRecord/DSFieldRecord';
import FilterUserAction from '../../../filterQueryFormat/FilterUserAction';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import devLogger from 'react-utils/devLogger';
import emptyFunction from 'react-utils/emptyFunction';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const QuickFiltersBarWrappingContainer = styled.div.withConfig({
  displayName: "QuickFiltersBar__QuickFiltersBarWrappingContainer",
  componentId: "sc-1rc0b7t-0"
})(["display:flex;flex-wrap:wrap;gap:8px;"]);
const SIZE_TYPES = {
  sm: 'sm',
  md: 'md'
};
const BUTTON_PROPS_FOR_SIZE_TYPE = {
  [SIZE_TYPES.sm]: smallButtonProperties,
  [SIZE_TYPES.md]: mediumButtonProperties
};
const QuickFiltersBar = ({
  disableTruncation,
  onClearAll: onClearAllProp,
  onUserAction = emptyFunction,
  persistClearAllButton,
  propertyNames,
  size
}) => {
  const {
    isLoadingViewerData,
    getTrackerInstance,
    props: {
      baseFilterFamily
    },
    quickFiltersInternalValue
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getField,
    getFields,
    getOperatorsForType,
    getFieldDefinitions
  } = useStrategy();
  const {
    getFieldLabelString
  } = useTranslationHelpers(baseFilterFamily);
  const onQuickFiltersChange = useQuickFiltersOnChangeHandler();
  const onValueChange = useOnQuickFilterValueChange(onUserAction, USED_LEGACY_FILTERS_BAR);
  const buttonProperties = size ? BUTTON_PROPS_FOR_SIZE_TYPE[size] : BUTTON_PROPS_FOR_SIZE_TYPE[SIZE_TYPES.sm];
  const fieldDefinitions = getFieldDefinitions(baseFilterFamily);
  const [allFields, setAllFields] = useState();
  useEffect(() => {
    const loadFields = async () => {
      const loadedFields = await getFields(baseFilterFamily);
      setAllFields(loadedFields);
    };
    void loadFields();
  }, [baseFilterFamily, getFields]);
  const excludedPropertyNames = useMemo(() => {
    return allFields ? propertyNames.filter(propertyName => !allFields.get(propertyName)) : [];
  }, [propertyNames, allFields]);
  useEffect(() => {
    if (excludedPropertyNames.length === 0) return;
    void (async () => {
      const results = await Promise.all(excludedPropertyNames.map(async propertyName => {
        const field = await getField(baseFilterFamily, propertyName);
        return DSFieldRecord.isDSFieldRecord(field);
      }));
      const invalidPropertyNames = excludedPropertyNames.filter((_, i) => !results[i]);
      if (invalidPropertyNames.length > 0) {
        console.warn(new Error(`Could not render missing properties for ${baseFilterFamily} in QuickFiltersBar: ${invalidPropertyNames.join()}`));
        devLogger.warn({
          key: 'Failed to fetch all properties',
          message: `Could not render missing properties for ${baseFilterFamily} in QuickFiltersBar: ${invalidPropertyNames.join()}`
        });
      }
    })();
  }, [excludedPropertyNames, baseFilterFamily, getField]);
  if (isLoadingViewerData || !allFields) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  const propertyRecords = propertyNames
  // filter out excluded properties
  .filter(propertyName => !excludedPropertyNames.includes(propertyName))
  // get DSFieldRecords
  .map(propertyName => allFields.get(propertyName)).filter(field => DSFieldRecord.isDSFieldRecord(field))
  // format DSFieldRecords for right label
  .map(field => field.set('displayName', getFieldLabelString(field)));
  const quickFiltersConditions = quickFiltersInternalValue ? quickFiltersInternalValue.conditions : OrderedMap();
  const quickFiltersComponents = propertyRecords.map(propertyRecord => {
    const [conditionKey, condition] = quickFiltersConditions.entrySeq().find(
    // @ts-expect-error ts-migrate(2322) FIXME
    ([, value]) => value.field && value.field.name === propertyRecord.name) || [];
    return {
      condition,
      keyPath: conditionKey ? List.of(conditionKey) : undefined,
      propertyRecord,
      QuickFilterComponent: getQuickFilterComponent({
        condition,
        filterFamily: baseFilterFamily,
        getOperatorsForType,
        propertyRecord,
        fieldDefinitions
      })
    };
  });
  const onClearAll = () => {
    var _getTrackerInstance;
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
    onQuickFiltersChange();
    if (typeof onClearAllProp === 'function') {
      onClearAllProp();
    }
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('clearedAllValues', {
      primaryObjectTypeId: baseFilterFamily,
      version: '1'
    });
    onUserAction(FilterUserAction({
      action: USED_LEGACY_FILTERS_BAR,
      subAction: ALL_FILTER_VALUES_CLEARED,
      filterFamily: baseFilterFamily
    }));
  };
  return /*#__PURE__*/_jsxs(QuickFiltersBarWrappingContainer, {
    children: [quickFiltersComponents.map(({
      condition,
      keyPath,
      propertyRecord,
      QuickFilterComponent
    }, index) => {
      if (!QuickFilterComponent) {
        return undefined;
      }
      const filterFamily = quickFiltersInternalValue ? quickFiltersInternalValue.filterFamily : baseFilterFamily;
      return (
        /*#__PURE__*/
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        _jsx(QuickFilterComponent, {
          buttonProperties: buttonProperties,
          condition: condition,
          disableTruncation: disableTruncation,
          filterFamily: filterFamily,
          isFirst: index === 0,
          property: propertyRecord,
          onValueChange: onValueChange(keyPath)
        }, `${propertyRecord.label}-quick-filter-select`)
      );
    }), (persistClearAllButton || !!quickFiltersConditions.size) && /*#__PURE__*/_jsx(Button, {
      onClick: onClearAll,
      size: buttonProperties.BUTTON_SIZE,
      use: buttonProperties.BUTTON_USE,
      "data-selenium-test": "QuickFiltersBar-clearAll",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.quickFiltersBar.clearAll"
      })
    })]
  });
};
export default QuickFiltersBar;